/**
 * assets/personalization-form.js  (v5 — self-healing across variant swaps)
 * ---------------------------------------------------------------------------
 * Same as v4, plus the actual fix for the disappearing-on-variant-change bug:
 *
 * Kalles refreshes only a SMALL fragment of the buy-box (price/quantity/Add
 * to Cart wrapper) via a fetch that returns just a few KB of HTML, then
 * replaces that wrapper's innerHTML directly. Because our block had been
 * physically moved inside that wrapper, it got wiped out -- and since the
 * fetched fragment never contained our block, there was nothing for v4's
 * MutationObserver to re-initialize.
 *
 * v5 keeps a permanent, in-memory reference to each personalization block
 * (never destroyed) and a registry of live instances. The MutationObserver
 * now also checks, after every DOM mutation, whether any known instance's
 * block has become disconnected from the document -- if so, it re-locates
 * the current product form (which may itself be a fresh node after the
 * swap) and re-inserts the SAME block + re-binds submit guards. Field
 * values and uploaded photo state are preserved because the node itself
 * is never recreated, just reattached.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];
  var ALLOWED_EXT = ['jpg', 'jpeg', 'png', 'webp'];
  var STICKY_SELECTOR = 'hdt-sticky-btn-atc, [class*="sticky-atc"], [class*="hdt-sticky"]';
  var CLONE_ATTR = 'data-pf-cloned';
  var instances = [];

  function isInSticky(form) {
    return !!form.closest(STICKY_SELECTOR);
  }

  function findProductForms(nearEl) {
    var scope = (nearEl && nearEl.closest && nearEl.closest('[data-section-id], product-form, .shopify-section')) || document;
    var forms = Array.prototype.slice.call(scope.querySelectorAll('form[action*="/cart/add"]'));
    if (!forms.length) {
      forms = Array.prototype.slice.call(document.querySelectorAll('form[action*="/cart/add"]'));
    }

    var nonSticky = forms.filter(function (f) { return !isInSticky(f); });
    var candidates = nonSticky.length ? nonSticky : forms;
    var withVariant = candidates.filter(function (f) { return f.querySelector('[name="id"]'); });
    var main = withVariant[0] || candidates[0] || null;

    var secondary = forms.filter(function (f) { return f !== main; });
    return { main: main, secondary: secondary };
  }

  function attachToForm(block, form) {
    if (!form) return;
    if (block.closest && block.closest('form') === form && block.isConnected) return;

    var submitBtn =
      form.querySelector('button[name="add"]') ||
      form.querySelector('[type="submit"]') ||
      form.querySelector('.shopify-payment-button') ||
      null;

    var insertionPoint = submitBtn;
    if (insertionPoint) {
      while (insertionPoint.parentElement && insertionPoint.parentElement !== form) {
        insertionPoint = insertionPoint.parentElement;
      }
    }

    if (insertionPoint && insertionPoint.parentNode === form) {
      form.insertBefore(block, insertionPoint);
    } else {
      form.appendChild(block);
    }
  }

  function readConfig(block) {
    return {
      productId: block.getAttribute('data-product-id'),
      imgbbKey: block.getAttribute('data-imgbb-key') || '',
      imgbbEndpoint: block.getAttribute('data-imgbb-endpoint') || 'https://api.imgbb.com/1/upload',
      useProxy: block.getAttribute('data-use-proxy') === 'true',
      proxyEndpoint: block.getAttribute('data-proxy-endpoint') || '',
      hideDynamicCheckout: block.getAttribute('data-hide-dynamic-checkout') !== 'false',
      maxImages: parseInt(block.getAttribute('data-max-images'), 10) || 3,
      maxFileSizeMb: parseFloat(block.getAttribute('data-max-file-size-mb')) || 5
    };
  }

  function validateFile(file, cfg) {
    var ext = (file.name.split('.').pop() || '').toLowerCase();
    if (ALLOWED_MIME.indexOf(file.type) === -1 && ALLOWED_EXT.indexOf(ext) === -1) {
      return 'Unsupported file type. Please upload a JPG, PNG, or WEBP image.';
    }
    var maxBytes = cfg.maxFileSizeMb * 1024 * 1024;
    if (file.size > maxBytes) {
      return 'File is too large. Maximum size is ' + cfg.maxFileSizeMb + ' MB.';
    }
    return null;
  }

  function fileToBase64(file) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function () {
        var result = reader.result || '';
        var base64 = String(result).split(',')[1] || '';
        resolve(base64);
      };
      reader.onerror = function () {
        reject(new Error('Could not read the selected file.'));
      };
      reader.readAsDataURL(file);
    });
  }

  function uploadImage(file, cfg, onProgress) {
    return fileToBase64(file).then(function (base64) {
      var formData = new FormData();
      formData.append('image', base64);

      var endpoint;
      if (cfg.useProxy && cfg.proxyEndpoint) {
        endpoint = cfg.proxyEndpoint;
      } else {
        if (!cfg.imgbbKey) {
          return Promise.reject(new Error('Missing ImgBB API key.'));
        }
        formData.append('key', cfg.imgbbKey);
        endpoint = cfg.imgbbEndpoint;
      }

      return new Promise(function (resolve, reject) {
        var xhr = new XMLHttpRequest();
        xhr.open('POST', endpoint, true);

        xhr.upload.onprogress = function (evt) {
          if (evt.lengthComputable && typeof onProgress === 'function') {
            onProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        };

        xhr.onload = function () {
          var json;
          try {
            json = JSON.parse(xhr.responseText);
          } catch (e) {
            reject(new Error('Upload failed: invalid response from image host.'));
            return;
          }
          if (xhr.status >= 200 && xhr.status < 300 && json && json.success && json.data && json.data.url) {
            resolve(json.data.url);
          } else {
            var msg = (json && json.error && json.error.message) || 'Image upload failed. Please try again.';
            reject(new Error(msg));
          }
        };

        xhr.onerror = function () {
          reject(new Error('Network error while uploading the image. Please check your connection and try again.'));
        };

        xhr.send(formData);
      });
    });
  }

  function PersonalizationForm(block) {
    this.block = block;
    this.cfg = readConfig(block);
    this.uploadList = block.querySelector('[data-pf-upload-list]');
    this.hiddenInputsWrap = block.querySelector('[data-pf-hidden-inputs]');
    this.fileInput = block.querySelector('[data-pf-file-input]');
    this.addBtn = block.querySelector('[data-pf-add-photo]');
    this.statusEl = block.querySelector('[data-pf-status]');
    this.nameInput = block.querySelector('[data-pf-required]');
    this.messageInput = block.querySelector('textarea[name="properties[Personalization - Message]"]');
    this.uploadErrorEl = block.querySelector('[data-pf-error="upload"]');
    this.nameErrorEl = block.querySelector('[data-pf-error="name"]');
    this.photos = [];
    this.nextId = 1;
    this.form = null;
    this.secondaryForms = [];

    this.bindEvents();
    this.reattach();
    instances.push(this);
  }

  /**
   * (Re)locates the current product form(s) and ensures `this.block` is a
   * live descendant of the main one, with submit guards bound. Safe to call
   * repeatedly -- idempotent via the __pfSubmitHooked flag on each form.
   */
  PersonalizationForm.prototype.reattach = function () {
    var forms = findProductForms(this.block);
    this.form = forms.main;
    this.secondaryForms = forms.secondary;

    if (this.form) {
      attachToForm(this.block, this.form);
      this.hookFormSubmit(this.form, true);
      if (this.cfg.hideDynamicCheckout) {
        this.hideDynamicCheckoutButtons(this.form);
      }
    }

    this.secondaryForms.forEach(function (secForm) {
      this.hookFormSubmit(secForm, false);
      if (this.cfg.hideDynamicCheckout) {
        this.hideDynamicCheckoutButtons(secForm);
      }
    }, this);

    this.syncSecondaryForms();
  };

  PersonalizationForm.prototype.bindEvents = function () {
    var self = this;
    this.addBtn.addEventListener('click', function () {
      if (self.photos.length >= self.cfg.maxImages) {
        self.showUploadError('You can upload up to ' + self.cfg.maxImages + ' images.');
        return;
      }
      self.fileInput.click();
    });

    this.fileInput.addEventListener('change', function (e) {
      var file = e.target.files && e.target.files[0];
      self.fileInput.value = '';
      if (!file) return;
      self.addPhoto(file);
    });

    if (this.nameInput) {
      this.nameInput.addEventListener('input', function () {
        self.setFieldError(self.nameErrorEl, null);
        self.syncSecondaryForms();
      });
    }
    if (this.messageInput) {
      this.messageInput.addEventListener('input', function () {
        self.syncSecondaryForms();
      });
    }
  };

  PersonalizationForm.prototype.showUploadError = function (msg) {
    this.setFieldError(this.uploadErrorEl, msg);
  };

  PersonalizationForm.prototype.setFieldError = function (el, msg) {
    if (!el) return;
    if (msg) {
      el.textContent = msg;
      el.hidden = false;
    } else {
      el.hidden = true;
      el.textContent = '';
    }
  };

  PersonalizationForm.prototype.setStatus = function (msg, isError) {
    if (!this.statusEl) return;
    if (!msg) {
      this.statusEl.hidden = true;
      return;
    }
    this.statusEl.hidden = false;
    this.statusEl.textContent = msg;
    this.statusEl.classList.toggle('personalization-form__status--error', !!isError);
  };

  PersonalizationForm.prototype.addPhoto = function (file) {
    var self = this;
    var error = validateFile(file, this.cfg);
    if (error) {
      this.showUploadError(error);
      return;
    }
    this.showUploadError(null);

    var id = this.nextId++;
    var index = this.photos.length;
    var row = this.buildRow(id, file, index);
    this.uploadList.appendChild(row.el);

    var photo = { id: id, file: file, url: null, status: 'uploading', error: null, row: row };
    this.photos.push(photo);
    this.renumberHiddenInputs();

    uploadImage(file, this.cfg, function (pct) {
      row.setProgress(pct);
    }).then(function (url) {
      photo.status = 'done';
      photo.url = url;
      row.setUploaded(url);
      self.renumberHiddenInputs();
    }).catch(function (err) {
      photo.status = 'error';
      photo.error = err.message;
      row.setError(err.message);
      self.renumberHiddenInputs();
    });
  };

  PersonalizationForm.prototype.removePhoto = function (id) {
    var idx = this.photos.findIndex(function (p) { return p.id === id; });
    if (idx === -1) return;
    var photo = this.photos[idx];
    photo.row.el.remove();
    this.photos.splice(idx, 1);
    this.renumberHiddenInputs();
    this.showUploadError(null);
  };

  PersonalizationForm.prototype.buildRow = function (id, file, index) {
    var self = this;
    var el = document.createElement('div');
    el.className = 'personalization-form__upload-row';

    var thumb = document.createElement('img');
    thumb.className = 'personalization-form__thumb';
    thumb.alt = file.name;
    var reader = new FileReader();
    reader.onload = function () { thumb.src = reader.result; };
    reader.readAsDataURL(file);

    var meta = document.createElement('div');
    meta.className = 'personalization-form__upload-meta';

    var label = document.createElement('span');
    label.className = 'personalization-form__upload-label';
    label.textContent = 'Photo ' + (index + 1);

    var name = document.createElement('span');
    name.className = 'personalization-form__upload-filename';
    name.textContent = file.name;

    var stateEl = document.createElement('span');
    stateEl.className = 'personalization-form__upload-state personalization-form__upload-state--uploading';
    stateEl.textContent = 'Uploading… 0%';

    meta.appendChild(label);
    meta.appendChild(name);
    meta.appendChild(stateEl);

    var progressBar = document.createElement('div');
    progressBar.className = 'personalization-form__progress';
    var progressFill = document.createElement('div');
    progressFill.className = 'personalization-form__progress-fill';
    progressBar.appendChild(progressFill);
    meta.appendChild(progressBar);

    var removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'personalization-form__remove-btn';
    removeBtn.setAttribute('aria-label', 'Remove image');
    removeBtn.textContent = 'Remove';
    removeBtn.addEventListener('click', function () { self.removePhoto(id); });

    el.appendChild(thumb);
    el.appendChild(meta);
    el.appendChild(removeBtn);

    return {
      el: el,
      setProgress: function (pct) {
        stateEl.textContent = 'Uploading… ' + pct + '%';
        progressFill.style.width = pct + '%';
      },
      setUploaded: function () {
        stateEl.textContent = 'Uploaded ✓';
        stateEl.className = 'personalization-form__upload-state personalization-form__upload-state--done';
        progressBar.remove();
      },
      setError: function (msg) {
        stateEl.textContent = 'Upload failed';
        stateEl.className = 'personalization-form__upload-state personalization-form__upload-state--error';
        progressBar.remove();
        var errLine = document.createElement('span');
        errLine.className = 'personalization-form__upload-error-line';
        errLine.textContent = msg;
        meta.appendChild(errLine);
      }
    };
  };

  PersonalizationForm.prototype.renumberHiddenInputs = function () {
    this.hiddenInputsWrap.innerHTML = '';
    this.photos.forEach(function (photo, i) {
      if (photo.status !== 'done' || !photo.url) return;
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_Personalization - Photo ' + (i + 1) + ']';
      input.value = photo.url;
      this.hiddenInputsWrap.appendChild(input);
    }, this);
    this.syncSecondaryForms();
  };

  PersonalizationForm.prototype.syncSecondaryForms = function () {
    if (!this.secondaryForms || !this.secondaryForms.length) return;
    var values = {};
    if (this.nameInput && this.nameInput.value.trim()) {
      values['properties[Personalization - Name]'] = this.nameInput.value.trim();
    }
    if (this.messageInput && this.messageInput.value.trim()) {
      values['properties[Personalization - Message]'] = this.messageInput.value.trim();
    }
    this.photos.forEach(function (photo, i) {
      if (photo.status === 'done' && photo.url) {
        values['properties[_Personalization - Photo ' + (i + 1) + ']'] = photo.url;
      }
    });

    this.secondaryForms.forEach(function (form) {
      var old = form.querySelectorAll('[' + CLONE_ATTR + ']');
      old.forEach(function (el) { el.remove(); });
      Object.keys(values).forEach(function (name) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = values[name];
        input.setAttribute(CLONE_ATTR, 'true');
        form.appendChild(input);
      });
    });
  };

  PersonalizationForm.prototype.getReadiness = function () {
    var nameVal = this.nameInput ? this.nameInput.value.trim() : '';
    if (this.nameInput && this.nameInput.hasAttribute('data-pf-required') && !nameVal) {
      return { ready: false, reason: 'name', message: 'Please enter the name to print/engrave.' };
    }
    var uploading = this.photos.some(function (p) { return p.status === 'uploading'; });
    if (uploading) {
      return { ready: false, reason: 'uploading', message: 'Please wait for your photo(s) to finish uploading.' };
    }
    var failed = this.photos.some(function (p) { return p.status === 'error'; });
    if (failed) {
      return { ready: false, reason: 'error', message: 'Please remove or re-upload the photo(s) that failed.' };
    }
    return { ready: true };
  };

  /** Idempotent: safe to call on the same form multiple times across reattach() calls. */
  PersonalizationForm.prototype.hookFormSubmit = function (form, isMain) {
    if (form.__pfSubmitHooked) return;
    form.__pfSubmitHooked = true;
    var self = this;
    form.addEventListener(
      'submit',
      function (e) {
        if (isMain) {
          self.syncSecondaryForms();
        }
        var readiness = self.getReadiness();
        if (!readiness.ready) {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (readiness.reason === 'name' && self.nameErrorEl) {
            self.setFieldError(self.nameErrorEl, readiness.message);
            if (self.nameInput) self.nameInput.focus();
          } else {
            self.showUploadError(readiness.message);
          }
          self.setStatus(readiness.message, true);
          self.block.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          self.setStatus(null);
        }
      },
      true
    );
  };

  PersonalizationForm.prototype.hideDynamicCheckoutButtons = function (form) {
    var selectors = ['.shopify-payment-button', '[data-shopify="payment-button"]'];
    selectors.forEach(function (sel) {
      var els = form.querySelectorAll(sel);
      els.forEach(function (el) {
        el.style.display = 'none';
        el.setAttribute('data-pf-hidden-dynamic-checkout', 'true');
      });
    });
  };

  function initAll(root) {
    var scope = root || document;
    var blocks = scope.querySelectorAll('[data-personalization-form]');
    blocks.forEach(function (block) {
      if (block.__personalizationInitialized) return;
      block.__personalizationInitialized = true;
      new PersonalizationForm(block);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  var pfObserverTimer = null;
  var pfObserver = new MutationObserver(function () {
    clearTimeout(pfObserverTimer);
    pfObserverTimer = setTimeout(function () {
      // 1) Pick up any brand-new personalization blocks rendered by the server.
      initAll(document);
      // 2) Self-heal: reattach any known instance whose block got detached
      //    by a partial AJAX HTML swap (e.g. Kalles' variant-change fetch).
      instances.forEach(function (inst) {
        if (!inst.block.isConnected) {
          inst.reattach();
        }
      });
    }, 80);
  });

  function startObserving() {
    pfObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.body) {
    startObserving();
  } else {
    document.addEventListener('DOMContentLoaded', startObserving);
  }
})();
